import { useContext, useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { SettingsContext } from '../store/SettingsContext';
import { UserContext } from '../store/UserContext';
import useAxios from './useAxios';
import useSubscriber from './useSubscriber';

export type HismithDeviceState = 'connecting' | 'connected' | 'offline' | 'error' | 'stopped';

export interface HismithDeviceStatus {
    state: HismithDeviceState;
    speed?: number;
    vspeed?: number;
    error?: string;
    updatedAt: string;
}

export interface HismithDeviceItem {
    deviceId: string;
    onlyfansId: number;
    nickName: string;
    productType: string;
    hasVibration: boolean;
    supportsClimax: boolean;
    idleSpeed: number;
    idleVibration: number;
    lastConnectedAt: string | null;
    lastError: string;
    tokenSuffix: string;
    status?: HismithDeviceStatus;
}

export type HismithDurationMode = 'dynamic' | 'fixed';

export interface HismithSettingItem {
    settingId?: string;
    onlyfansId: number;
    /** null = any device */
    deviceId: string | null;
    startDollarAmount: number;
    endingDollarAmount: number;
    durationMode: HismithDurationMode;
    durationPerDollar: number;
    speed: number;
    vibrationSpeed: number | null;
    climax: boolean;
}

export interface HismithDevicePatch {
    nickName?: string;
    idleSpeed?: number;
    idleVibration?: number;
    link?: string;
}

export const deviceLabel = (device: HismithDeviceItem): string => device.nickName || device.productType || `Device …${device.tokenSuffix}`;

const useHismith = (autoRefetch = false) => {
    const userContext = useContext(UserContext);
    const settingsContext = useContext(SettingsContext);
    const { data: subscriber } = useSubscriber();
    const axios = useAxios();
    const queryClient = useQueryClient();

    const [isServiceStatusLoading, setIsServiceStatusLoading] = useState<boolean>(false);
    const [isAddingDevice, setIsAddingDevice] = useState<boolean>(false);
    const [isUpdatingDevice, setIsUpdatingDevice] = useState<boolean>(false);
    const [isDeletingDevice, setIsDeletingDevice] = useState<boolean>(false);
    const [isSendingTest, setIsSendingTest] = useState<boolean>(false);

    const base = (): string => `${settingsContext.routes.hismith.base}${subscriber._id}`;

    const fetchDevices = async (): Promise<HismithDeviceItem[] | null> =>
        axios
            .get(`${base()}/devices`)
            .then(response => response.data)
            .catch(() => null);

    const devices = useQuery(['hismithDevices', subscriber?._id], () => fetchDevices(), {
        refetchOnWindowFocus: false,
        staleTime: 5 * 60 * 1000,
        refetchInterval: autoRefetch ? 5 * 1000 : false,
        enabled: userContext.jwtToken && settingsContext.apiKey && subscriber && subscriber._id ? true : false,
    });

    const getServiceActive = async (): Promise<boolean> => {
        setIsServiceStatusLoading(true);

        return axios
            .get(`${base()}/active`)
            .then(response => response.data.active === true)
            .catch(() => false)
            .finally(() => setIsServiceStatusLoading(false));
    };

    const setServiceActive = async (active: boolean): Promise<boolean> => {
        setIsServiceStatusLoading(true);

        return axios
            .post(`${base()}/active`, { active })
            .then(response => response.data.success === true)
            .catch(() => false)
            .finally(() => setIsServiceStatusLoading(false));
    };

    const addDevice = async (link: string, nickName: string): Promise<HismithDeviceItem> => {
        setIsAddingDevice(true);

        return axios
            .post(`${base()}/devices`, { link, nickName })
            .then(response => {
                devices.refetch();

                return response.data as HismithDeviceItem;
            })
            .catch(error => {
                throw new Error(error?.response?.data?.message || error?.response?.data || error.message || 'Failed to add device.');
            })
            .finally(() => setIsAddingDevice(false));
    };

    const updateDevice = async (deviceId: string, patch: HismithDevicePatch): Promise<boolean> => {
        setIsUpdatingDevice(true);

        return axios
            .patch(`${base()}/devices/${deviceId}`, patch)
            .then(response => {
                devices.refetch();

                return response.data.success === true;
            })
            .catch(error => {
                throw new Error(error?.response?.data?.message || error?.response?.data || error.message || 'Failed to update device.');
            })
            .finally(() => setIsUpdatingDevice(false));
    };

    const deleteDevice = async (deviceId: string): Promise<boolean> => {
        setIsDeletingDevice(true);

        return axios
            .delete(`${base()}/devices/${deviceId}`)
            .then(response => {
                devices.refetch();
                // Ranges targeting the deleted device are removed server-side.
                queryClient.invalidateQueries(['hismithSettings', subscriber?._id]);

                return response.data.success === true;
            })
            .catch(() => false)
            .finally(() => setIsDeletingDevice(false));
    };

    const sendTestCommand = async (deviceId: string): Promise<boolean> => {
        setIsSendingTest(true);

        return axios
            .post(`${base()}/devices/${deviceId}/test`)
            .then(response => response.data.success === true)
            .catch(error => {
                throw error;
            })
            .finally(() => setIsSendingTest(false));
    };

    return {
        ...devices,
        getServiceActive,
        setServiceActive,
        isServiceStatusLoading,
        addDevice,
        isAddingDevice,
        updateDevice,
        isUpdatingDevice,
        deleteDevice,
        isDeletingDevice,
        sendTestCommand,
        isSendingTest,
    };
};

export default useHismith;
